import React, { useState, useEffect, Fragment } from 'react';
import { getRequest } from '../../globalhelper/helper';
import {
    Box,
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CSVLink } from 'react-csv';
import moment from 'moment';

const FunctionalProfitAndLoss = () => {
    const [companyDetails, setCompanyDetails] = useState([]);
    const [companyName, setCompanyName] = useState([]);
    const [formData, setFormData] = useState({
        fromDate: moment().startOf('month'),
        toDate: moment().endOf('month'),
        company: null,
        theme: "Theme1"
    });
    const [apiResponse, setApiResponse] = useState([]);
    const [customApiResponse, setCustomApiResponse] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);

    const handleShowModal = () => setShowModal(true);
    const handleCloseModal = () => setShowModal(false);

    useEffect(() => {
        getRequest(`api/edureka/getCompanyAssignedToUser`).then(response => {
            const responseData = response.res.data;
            const orgname = responseData.map(item => item.orgname);
            setCompanyDetails(responseData);
            setCompanyName(orgname);
            if (orgname.length === 1) {
                setFormData(prevState => ({ ...prevState, company: orgname[0] }));
            }
        }).catch(err => console.log(err));
    }, []);

    const handleDateChange = (name, value) => {
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleAutocompleteChange = (name, value) => {
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleSubmit = () => {
        const { fromDate, toDate, company, theme } = formData;
        if (fromDate && toDate && company && theme) {
            setLoading(true);
            const id = companyDetails.find(item => item.orgname === company)?.orgid;
            const fDate = moment(fromDate).format('YYYY-MM-DD');
            const tDate = moment(toDate).format('YYYY-MM-DD');
            getRequest(`api/edureka/getFunctionalProfitAndLoss?fromDate=${fDate}&toDate=${tDate}&company=${id}&theme=${theme}`)
            .then(response => {
                const responseData = response.res.data.processed_data;
                setLoading(false);
    
                const updatedResponse = responseData.map(row => {
                    const formattedRow = {};
                    let grandTotal = 0;
    
                    for (const key in row) {
                        formattedRow[key] = typeof row[key] === 'number' ? row[key].toFixed(2) : row[key];
                        if (["B2C_Total", "B2B_Total", "HED_Total", "PGP_Total", "ELC_Total", "VLAB_Total", "UnIdentified_Total"].includes(key)) {
                            grandTotal += parseFloat(row[key]) || 0;
                        }
                    }
                    formattedRow["GrandTotal"] = grandTotal.toFixed(2);
                    return formattedRow;
                });
    
                setApiResponse(updatedResponse);
            })
            .catch(err => {
                setLoading(false);
                console.log(err);
                toast.error("Oops something went wrong");
            });
        } else {
            toast.error("Please select all the required fields");
        }
    };
    const handleCustomButtonClick = () => {
        const { fromDate, toDate, company } = formData;
        if (fromDate && toDate && company) {
            const id = companyDetails.find(item => item.orgname === company)?.orgid;
            const fDate = moment(fromDate).format('YYYY-MM-DD');
            const tDate = moment(toDate).format('YYYY-MM-DD');
            if (id) {
                getRequest(`api/edureka/reconReport?fromDate=${fDate}&toDate=${tDate}&orgid=${id}`)
                    .then(response => {
                        const customData = JSON.parse(response.res.data);
                        setCustomApiResponse(customData.map(row => {
                            const formattedRow = {};
                            for (const key in row) {
                                formattedRow[key] = typeof row[key] === 'number' ? row[key].toFixed(2) : row[key];
                            }
                            return formattedRow;
                        }));
                        handleShowModal(); // Show the modal
                    })
                    .catch(err => {
                        console.log(err);
                        toast.error("Error in sending data");
                    });
            } else {
                toast.error("Company ID not found");
            }
        } else {
            toast.error("Please select all the required fields");
        }
    };

    const columnNames = Object.keys(apiResponse[0] || {});

    const staticColumnHeaders = [
      "ApprovalStatus",
      "transactiontype",
      "createdid",
      "category",
      "ExpenseAccounted",
      "UnAllocatedAmount",
      "AllocationAmount",
      "diff"
    ];
    const uniqueColumnNames = [...new Set([...columnNames, "GrandTotal"])];

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3 }}>Functional Profit And Loss</Typography>
                
                <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Selection Filters</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                                <Autocomplete
                                    size="small"
                                    options={companyName.sort()}
                                    value={formData.company}
                                    onChange={(event, newValue) => handleAutocompleteChange('company', newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Company" variant="outlined" fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={2.5}>
                                <DatePicker
                                    label="From Date"
                                    value={formData.fromDate}
                                    onChange={(newValue) => handleDateChange('fromDate', newValue)}
                                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                />
                            </Grid>
                            <Grid item xs={12} md={2.5}>
                                <DatePicker
                                    label="To Date"
                                    value={formData.toDate}
                                    onChange={(newValue) => handleDateChange('toDate', newValue)}
                                    renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                                />
                            </Grid>
                            <Grid item xs={12} md={2.5}>
                                <Autocomplete
                                    size="small"
                                    options={["Theme1"]}
                                    getOptionLabel={(option) => option === "Theme1" ? "Cost Allocation Theme" : option}
                                    value={formData.theme}
                                    onChange={(event, newValue) => handleAutocompleteChange('theme', newValue)}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Theme" variant="outlined" fullWidth />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={1.5}>
                                <Button 
                                    variant="contained" 
                                    color="primary" 
                                    fullWidth 
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
                                </Button>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    {apiResponse.length > 0 && (
                        <>
                            <Button 
                                variant="outlined" 
                                color="info" 
                                size="small"
                                onClick={handleCustomButtonClick}
                            >
                                Show Recon (FinAc&CostAllocation)
                            </Button>
                            <CSVLink 
                                data={apiResponse} 
                                filename='Functional_Profit_And_Loss.csv' 
                                style={{ textDecoration: 'none' }}
                            >
                                <Button 
                                    variant="outlined" 
                                    color="success" 
                                    size="small"
                                    startIcon={<CloudDownloadIcon />}
                                >
                                    Download
                                </Button>
                            </CSVLink>
                        </>
                    )}
                </Box>

                {apiResponse.length > 0 && (
                    <TableContainer component={Paper} sx={{ mt: 4, maxHeight: 600 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {uniqueColumnNames.map((column, index) => (
                                        <TableCell key={index} sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                            {column}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {apiResponse.map((row, rowIndex) => (
                                    <TableRow key={rowIndex} hover>
                                        {uniqueColumnNames.map((column, columnIndex) => (
                                            <TableCell key={columnIndex}>
                                                {row[column] || ""}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <Dialog 
                    open={showModal} 
                    onHide={handleCloseModal} 
                    maxWidth="xl" 
                    fullWidth
                    onClose={handleCloseModal}
                >
                    <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Recon (FinAc&CostAllocation)
                        <IconButton onClick={handleCloseModal}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                        {customApiResponse.length > 0 ? (
                            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {staticColumnHeaders.map((column, index) => (
                                                <TableCell key={index} sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                                                    {column}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {customApiResponse.map((row, rowIndex) => (
                                            <TableRow key={rowIndex} hover>
                                                {staticColumnHeaders.map((column, columnIndex) => (
                                                    <TableCell key={columnIndex}>
                                                        {row[column] || ""}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography>No data available</Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <CSVLink data={customApiResponse} filename='Recon (FinAc&CostAllocation).csv' style={{ textDecoration: 'none' }}>
                            <Button variant="contained" color="success" startIcon={<CloudDownloadIcon />}>
                                Download
                            </Button>
                        </CSVLink>
                        <Button onClick={handleCloseModal} color="secondary">
                            Close
                        </Button>
                    </DialogActions>
                </Dialog>

                <ToastContainer />
            </Box>
        </LocalizationProvider>
    );
};

export default FunctionalProfitAndLoss;
